import { useState } from "react"

import { Checkbox, Flex, Tooltip, Typography } from "antd"
import cls from "classnames"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import ButtonComponent from "../../../../Components/Button"
import Private from "../../../../Components/Private"
import Icon from "../../../../Icon"
import Images from "../../../../Images"
import AppUtils, {
  dateFormat,
  formatCurrency,
  pinIconColor,
  serviceImg,
  serviceMap
} from "../../../../Utils/utils"
import { isAttentionAlert } from "../../hooks/useBulkAction"
import AlertStatusTag from "../AlertStatusTag/AlertStatusTag"

import "./AlertCard.less"

const { Text } = Typography

const AlertCard = ({
  alert,
  handleSelectRow,
  handleChangeStatus,
  disabledStatus = false,
  isBulkActionActive = false,
  selected = false,
  onToggleSelect
}) => {
  const { theme } = useSelector((state) => state.nav)
  const { t } = useTranslation()

  const link = alert?.transaction_link?.[0] || alert?.transaction_url?.[0]
  const isSelectable = isAttentionAlert(alert)

  const handleViewTransaction = () => {
    if (!link) return
    AppUtils.goLink(link, "_blank")
  }

  return (
    <Flex
      vertical
      gap={16}
      className={cls("alert-card", { "alert-card-selected": isBulkActionActive && selected })}
    >
      <Flex align="center" justify="space-between" gap={10}>
        <Flex align="center" gap={8}>
          {isBulkActionActive && (
            <Checkbox
              checked={selected}
              disabled={!isSelectable}
              onChange={() => onToggleSelect?.(alert?.alert_id)}
            />
          )}
          <div className="grey-block">
            <img
              width={16}
              height={16}
              src={Images[serviceImg?.[theme]?.[alert?.alert_service]]}
              alt={alert?.alert_service}
            />
            <Text strong>{serviceMap[alert?.alert_service]}</Text>
          </div>
        </Flex>
        <Flex gap={4}>
          <CopyBtn alert={alert} />
          <PinComponent
            alert={alert}
            theme={theme}
            t={t}
            onPin={(alert) => handleSelectRow(alert, "pin")}
          />
        </Flex>
      </Flex>
      <div className="fields-list">
        <div className="data-block">
          <Text type="secondary" strong>
            Amount
          </Text>
          <CopyableValue
            value={<Private>{formatCurrency(alert?.amount)}</Private>}
            copyValue={formatCurrency(alert?.amount)}
            className="amount"
          />
        </div>
        <div className="data-block">
          <Text type="secondary" strong ellipsis>
            Transaction Date
          </Text>
          <Text strong ellipsis>
            {dayjs(alert?.transaction_timestamp).format(dateFormat)}
          </Text>
        </div>
        <div className="data-block">
          <Text type="secondary" strong ellipsis>
            Card
          </Text>
          <CardLastFour alert={alert} theme={theme} />
        </div>
        <div className="data-block">
          <Text type="secondary" strong ellipsis>
            Alert Date
          </Text>
          <Text strong ellipsis>
            {dayjs(alert?.alert_timestamp).format(dateFormat)}
          </Text>
        </div>
        <div className="data-block">
          <Text type="secondary" strong ellipsis>
            ARN
          </Text>
          {alert?.arn ? (
            <CopyableValue value={<Private>{alert?.arn}</Private>} copyValue={alert?.arn} />
          ) : (
            <Text strong>-</Text>
          )}
        </div>
        <div className="data-block">
          <Text type="secondary" strong ellipsis>
            Auth Code
          </Text>
          {alert?.auth_code ? (
            <CopyableValue
              value={<Private>{alert?.auth_code}</Private>}
              copyValue={alert?.auth_code}
            />
          ) : (
            <Text strong>-</Text>
          )}
        </div>
      </div>
      <Flex vertical gap={12}>
        <AlertStatusTag
          alert={alert}
          handleChangeStatus={handleChangeStatus}
          isDemo={false}
          disabledStatus={disabledStatus}
        />
        <Flex gap={12}>
          <ButtonComponent
            style={{ height: "36px", flex: 1 }}
            onClick={() => handleSelectRow(alert, "view_details")}
          >
            View details
          </ButtonComponent>
          {alert?.payment_processor && alert?.payment_processor !== "-" && (
            <Tooltip title={!link ? "Transaction link unavailable" : ""}>
              <ButtonComponent
                style={{ height: "36px", flex: 1 }}
                onClick={handleViewTransaction}
                disabled={!link}
              >
                View transaction
              </ButtonComponent>
            </Tooltip>
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}

const PinComponent = ({ alert, theme, t, onPin }) => {
  const pinned = alert?.pinned
  if (alert?.["alert_status"]?.toLowerCase() === "attention") {
    return <Icon type={"clock"} style={{ fontSize: "20px" }} />
  } else {
    return (
      <Tooltip title={t(pinned ? "Unpin" : "Pin")}>
        <button
          type="text"
          onClick={(e) => {
            e.stopPropagation()
            onPin(alert)
          }}
          className={cls("icon-btn", { active: pinned })}
        >
          <Icon color={pinIconColor[theme]} type={pinned ? "pin_active" : "pin"} />
        </button>
      </Tooltip>
    )
  }
}

const CopyBtn = ({ alert }) => {
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    navigator.clipboard.writeText(alert?.alert_id)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Tooltip title={copied ? "Copied!" : "Copy Alert ID"}>
      <button
        type="text"
        onClick={(e) => {
          e.stopPropagation()
          handleCopy()
        }}
        className={cls("icon-btn")}
        style={{ padding: 0 }}
      >
        <Icon type={"link"} />
      </button>
    </Tooltip>
  )
}

const CopyableValue = ({ value, copyValue = value, className }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = (e) => {
    e.stopPropagation()
    navigator.clipboard.writeText(copyValue)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="copy-row">
      <Text strong className={cls("copy-row-text", className)} title={copyValue}>
        {value}
      </Text>
      <button type="button" className="copy-row-btn" onClick={handleCopy}>
        {copied ? <Icon type={"check"} style={{ color: "#52c41a" }} /> : <Icon type={"copy"} />}
      </button>
    </div>
  )
}

const CardLastFour = ({ alert, theme }) => {
  const _ = alert?.card_last4
  return (
    <Flex align="center" gap={8} style={{ minWidth: 0 }}>
      {alert?.brand && (
        <img
          src={Images[`${alert?.brand?.toLowerCase()}${theme === "dark" ? "_dark" : ""}`]}
          width={20}
          height={20}
          alt={alert?.brand}
          style={{ flexShrink: 0 }}
        />
      )}
      {_ && alert?.card_bin ? (
        <CopyableValue
          value={<Private mask="••••">{alert?.card_bin + " ••• " + _}</Private>}
          copyValue={alert?.card_last4}
        />
      ) : (
        _ && <CopyableValue value={<Private mask="••••">{`•••• ${_}`}</Private>} copyValue={_} />
      )}
    </Flex>
  )
}

export default AlertCard
